import styled from "@emotion/styled";
import { Box } from "@mui/system";

export const RackPricingDGStyle = {
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#FFF59D" : "#4a4327", // Light yellow in light mode, dark gold in dark mode
    fontWeight: "bold",
    color: (theme) => `${theme.palette.mode === "light" ? "black" : "white"} !important`, // Black text in light mode, white in dark mode
    "&:hover": {
      backgroundColor: (theme) =>
        theme.palette.mode === "light" ? "#FFF176" : "#5a5233", // Slightly brighter on hover
    },
  },
  // Prior columns (Prior Cost, Prior Markup, Prior Rack)
  // Header text color for those specific columns
  "& .MuiDataGrid-columnHeader.prior-header": {
    color: (theme) => (theme.palette.mode === "light" ? "#1565c0" : "#90caf9"), // medium/light blue
    fontWeight: 700,
  },
  // Cell text color for those specific columns
  "& .MuiDataGrid-cell.prior-cell": {
    color: (theme) => (theme.palette.mode === "light" ? "#1e88e5" : "#90caf9"), // medium/light blue
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#f5f5f5" : "#333",
    "& .MuiDataGrid-columnHeader": {
      fontWeight: 700,
      fontSize: "var(--font-scale-lg)",
      paddingTop: "8px",
      paddingBottom: "8px",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      fontSize: "var(--font-scale-lg)",
    },
  },
  "& .suggested-cell": {
    cursor: "pointer",
    userSelect: "none",
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#e8f4f8" : "#173138",
    "&:hover": {
      backgroundColor: (theme) =>
        theme.palette.mode === "light" ? "#d0ebf2" : "#1e3e47",
    },
  },
  // Ensure suggested cell matches selected row background when selected
  "& .Mui-selected .suggested-cell": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#FFF59D" : "#4a4327",
    "&:hover": {
      backgroundColor: (theme) =>
        theme.palette.mode === "light" ? "#FFF176" : "#5a5233",
    },
  },
  "& .outage-cell": {
    paddingTop: "7px",
  },
  "& .racknote-cell": {
    paddingTop: "7px",
  },
  "& .dtn-cell": {
    paddingTop: "7px",
  },
  "& .MuiDataGrid-cell:focus": {
    outline: "none",
  },
  "& .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-cell": {
    color: (theme) => (theme.palette.mode === "light" ? "inherit" : "#ffffff"),
  },
  "& .negative-value": {
    color: (theme) => (theme.palette.mode === "light" ? "red" : "#ff6b6b"),
  },
  "& .editable-cell": {
    position: "relative",
    cursor: "pointer",
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#fff" : "#333",
    border: (theme) =>
      `1px solid ${theme.palette.mode === "light" ? "#e0e0e0" : "#555"}`,
    "&:hover": {
      backgroundColor: (theme) =>
        theme.palette.mode === "light" ? "#fff" : "#333",
      border: (theme) =>
        `1px solid ${theme.palette.mode === "light" ? "#1976d2" : "#90caf9"}`,
      transform: "scale(1.02)",
      transition: "all 0.2s ease",
    },
  },
  "& .editable-header": {
    fontWeight: "bold",
    color: (theme) => (theme.palette.mode === "light" ? "#0000CD" : "#4d94ff"),
  },
  "& .Mui-selected .editable-cell": {
    border: (theme) =>
      `2px solid ${theme.palette.mode === "light" ? "#1976d2" : "#90caf9"}`,
    borderRadius: "4px",
    padding: "0 8px",
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#FFF59D" : "#4a4327",
    boxShadow: (theme) =>
      theme.palette.mode === "light"
        ? "0 2px 4px rgba(0,0,0,0.1)"
        : "0 2px 4px rgba(0,0,0,0.3)",
    transform: "translateY(-1px)",
    transition: "all 0.2s ease",
    zIndex: 1,
  },
  "& .Mui-selected .editable-cell:hover": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#FFF176" : "#5a5233",
  },
  // Style for edited cells (show visual indicator when cell has been modified)
  "& .edited-cell": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#ffebee" : "#4a2c2c", // Light red background
    border: (theme) =>
      `2px solid ${theme.palette.mode === "light" ? "#f44336" : "#ff6b6b"}`, // Red border
    position: "relative",
    "&::after": {
      content: "'*'",
      position: "absolute",
      top: "2px",
      right: "4px",
      color: (theme) => (theme.palette.mode === "light" ? "#d32f2f" : "#ff6b6b"),
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "1",
    },
  },
  // Ensure edited cells maintain their styling when selected
  "& .Mui-selected .edited-cell": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#ffcdd2" : "#5a3535", // Slightly darker red when selected
    border: (theme) =>
      `2px solid ${theme.palette.mode === "light" ? "#d32f2f" : "#ff8a80"}`, // Maintain red border when selected
  },
  "& .Mui-selected .edited-cell:hover": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#ffb3ba" : "#634040", // Even darker on hover when selected
  },

  // Style for error cells
  "& .error-cell": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#fbe7d3ff" : "#9e6853ff",
    border: (theme) =>
      `2px solid ${theme.palette.mode === "light" ? "#e9600bff" : "#ff6b6b"}`,
    position: "relative",
    "&::after": {
      content: "'!'",
      position: "absolute",
      top: "2px",
      right: "4px",
      color: (theme) =>
        theme.palette.mode === "light" ? "#e9600bff" : "#ff6b6b",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "1",
    },
  },

  // Error cell when selected
  "& .Mui-selected .error-cell": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#fcad6cff" : "#5a3535",
    border: (theme) =>
      `2px solid ${theme.palette.mode === "light" ? "#FB8C00" : "#ff8a80"}`,
  },

  // Error cell hover
  "& .error-cell:hover": {
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "#ed946eff" : "#f89e77ff",
  },

  // Hide number input spinners (up/down arrows)
  "& input[type=number]": {
    "-moz-appearance": "textfield",
    fontSize: "16px", // Larger font size when typing
    fontWeight: "500",
    "&::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
    "&::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
  // Style for editable cell input when in edit mode
  "& .MuiDataGrid-cell--editing input": {
    fontSize: "16px",
    fontWeight: "500",
    padding: "4px 8px",
  },
  "& .hide-header .MuiDataGrid-columnHeaderTitle": {
    visibility: "hidden",
  },
  "& .MuiDataGrid-columnHeader": {
    cursor: "default !important",
  },
};

const ControlWrapper = styled(Box)`
  display: flex;
  flex: unset;

  @media (max-width: 1400px) {
    flex-wrap: wrap;
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  gap: 12px;
  flex: auto;

  @media (max-width: 550px) {
    flex-direction: column;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;  
  @media (max-width: 650px) {
    flex-wrap: wrap;
  }
`;

export { ButtonWrapper, SearchWrapper, ControlWrapper };
